import { useScrollReveal } from '@/hooks/useScrollReveal';
import { useTranslation } from 'react-i18next';

const STAT_ICONS = ['ri-restaurant-line', 'ri-map-2-line', 'ri-timer-line', 'ri-money-dollar-circle-line'];

export default function OrangeCountyData() {
  const leftRef = useScrollReveal<HTMLDivElement>();
  const rightRef = useScrollReveal<HTMLDivElement>();
  const { t } = useTranslation();

  const steps = t('operate.steps', { returnObjects: true }) as {
    title: string;
    desc: string;
  }[];
  const stats = t('operate.stats', { returnObjects: true }) as { value: string; label: string }[];

  return (
    <section id="operate" className="py-24 bg-teal-50">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block bg-teal-50 text-teal-600 text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full mb-4">
            {t('operate.tag')}
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
            {t('operate.title1')}<br />
            <span className="text-teal-500">{t('operate.title2')}</span>
          </h2>
          <p className="mt-5 text-gray-500 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            {t('operate.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left: Image + Stats */}
          <div ref={leftRef} className="reveal-on-scroll animate-fade-in-left">
            <div className="relative rounded-[28px] overflow-hidden w-full h-[420px] md:h-[480px]">
              <img
                src="https://readdy.ai/api/search-image?query=food%20truck%20entrepreneur%20reviewing%20permit%20paperwork%20and%20regulatory%20documents%20at%20a%20bright%20modern%20office%20desk%20with%20laptop%20and%20city%20seal%20stamps%20visible%20organized%20workspace%20clean%20contemporary%20interior%20design%20bright%20natural%20overhead%20lighting%20authentic%20documentary%20style&width=700&height=500&seq=ocdata3&orientation=landscape"
                alt="Food truck build planning and permits"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 flex flex-wrap gap-3">
                {stats.map((s, i) => (
                  <div
                    key={s.label}
                    className="flex items-center gap-2 bg-white/90 backdrop-blur-sm rounded-xl px-4 py-2.5 shadow-sm"
                  >
                    <div className="w-8 h-8 flex items-center justify-center bg-teal-50 rounded-lg text-teal-500">
                      <i className={`${STAT_ICONS[i]} text-sm`} />
                    </div>
                    <div>
                      <div className="text-gray-900 text-xs font-bold">{s.value}</div>
                      <div className="text-gray-400 text-[10px] leading-tight">{s.label}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Steps */}
          <div ref={rightRef} className="reveal-on-scroll animate-fade-in-right flex flex-col gap-5">
            {steps.map((step, i) => (
              <div
                key={i}
                className="flex gap-5 bg-white rounded-2xl p-5 hover:shadow-sm transition-shadow duration-200"
              >
                <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center rounded-xl bg-teal-500 text-white font-bold text-sm">
                  {String(i + 1).padStart(2, '0')}
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 text-base mb-1">{step.title}</h4>
                  <p className="text-gray-500 text-sm leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}